import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPencil } from 'react-icons/hi';
import { storesAPI } from '../services/api';
import { notify } from '../utils/notify';
import { mensajeDeError } from '../utils/apiError';
import { comisionDe } from '../utils/comision';
import { dinero } from '../utils/economia';

/**
 * La comisión que se lleva este destino de cada venta, y cómo cambiarla.
 *
 * Vive en la tarjeta de su tienda y no en Ajustes porque es un dato de ese
 * destino: una cuenta de eBay y una tienda Shopify cobran distinto, y el
 * control va donde está lo que describe.
 *
 * Los valores por defecto son un punto de partida. Las tarifas cambian por
 * categoría, país y tipo de cuenta, así que aquí se dice cuál se está usando y
 * se deja poner la tuya. Al guardar, el servidor recalcula la ganancia de todos
 * los productos de esta tienda.
 */
export default function ComisionDeLaTienda({ store, onGuardada }) {
    const { t } = useTranslation();
    const comision = comisionDe(store.platform, store.settings);

    const [editando, setEditando] = useState(false);
    const [porcentaje, setPorcentaje] = useState('');
    const [fija, setFija] = useState('');
    const [guardando, setGuardando] = useState(false);
    const campo = useRef(null);

    // `autoFocus` solo actúa al montar el campo; aquí el campo aparece al
    // pulsar el lápiz, así que el foco se pone cuando ya existe.
    useEffect(() => {
        if (editando) campo.current?.focus();
    }, [editando]);

    // Un destino que la aplicación no sabe clasificar no inventa una comisión.
    if (!comision) return null;

    const abrir = () => {
        setPorcentaje(String(comision.porcentaje));
        setFija(String(comision.fija));
        setEditando(true);
    };

    const guardar = async (nueva) => {
        setGuardando(true);
        try {
            const respuesta = await storesAPI.update(store.id, { settings: { comision: nueva } });
            const { store: actualizada, recalculo } = respuesta.data.data;

            // El servidor dice si pudo recalcular las ganancias guardadas. Si
            // no, la comisión sí quedó guardada, y eso también se dice.
            if (recalculo && recalculo.fallidos === null) {
                notify.error(t('stores.fee_saved_recalc_failed'));
            } else {
                notify.success(t('stores.fee_saved', { count: recalculo?.recalculados ?? 0 }));
            }

            onGuardada({ ...store, ...actualizada });
            setEditando(false);
        } catch (error) {
            notify.error(mensajeDeError(error, t('stores.fee_save_failed')));
        } finally {
            setGuardando(false);
        }
    };

    const pct = Number(porcentaje);
    const fijo = fija === '' ? 0 : Number(fija);
    const valido =
        porcentaje !== '' && Number.isFinite(pct) && pct >= 0 && pct <= 50 &&
        Number.isFinite(fijo) && fijo >= 0 && fijo <= 20;

    if (!editando) {
        return (
            <div className="mt-2 flex items-start justify-between gap-2">
                <p className="text-xs text-slate-500">
                    {t('stores.fee_label', {
                        percent: comision.porcentaje,
                        fixed: dinero(comision.fija)
                    })}{' '}
                    <span className="text-slate-400">
                        ({comision.origen === 'tienda' ? t('stores.fee_custom') : t('stores.fee_default')})
                    </span>
                </p>
                <button
                    type="button"
                    onClick={abrir}
                    className="icon-edit shrink-0"
                    title={t('stores.fee_edit')}
                    aria-label={t('stores.fee_edit')}
                >
                    <HiPencil className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">
                    {t('stores.fee_percent')}
                    <input
                        ref={campo}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="50"
                        step="0.1"
                        value={porcentaje}
                        onChange={(e) => setPorcentaje(e.target.value)}
                        className="input mt-1"
                    />
                </label>
                <label className="text-xs text-slate-500">
                    {t('stores.fee_fixed')}
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="20"
                        step="0.01"
                        value={fija}
                        onChange={(e) => setFija(e.target.value)}
                        className="input mt-1"
                    />
                </label>
            </div>
            <p className="text-[11px] text-slate-400">{t('stores.fee_help')}</p>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => guardar({ porcentaje: pct, fija: fijo })}
                    disabled={!valido || guardando}
                    className="btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {t('stores.fee_save')}
                </button>
                <button
                    type="button"
                    onClick={() => setEditando(false)}
                    disabled={guardando}
                    className="btn-secondary btn-sm"
                >
                    {t('common.cancel')}
                </button>
                {/* Solo tiene sentido si hay una propia que quitar. */}
                {comision.origen === 'tienda' && (
                    <button
                        type="button"
                        onClick={() => guardar(null)}
                        disabled={guardando}
                        className="btn-secondary btn-sm"
                    >
                        {t('stores.fee_reset')}
                    </button>
                )}
            </div>
        </div>
    );
}
